/**
 * Meta Pixel + Conversions API client facade.
 *
 * One module owns all Meta-platform analytics so:
 *   - App code never imports the pixel snippet directly (parallel
 *     pattern to {@link import("./telemetry").track} for GA4 +
 *     Cloud Error Reporting).
 *   - Tests stay vendor-free — when `VITE_META_PIXEL_ID` is missing,
 *     every export becomes a safe no-op.
 *   - The Pixel <-> Conversions-API dedup is centralized: both wire
 *     formats share the same `event_id`, so a single conversion fired
 *     from both the browser (Pixel) and the server (Cloud Function
 *     `metaCapiTrack`) is counted exactly once by Meta.
 *
 * Wiring:
 *   1. `VITE_META_PIXEL_ID` = the numeric pixel id from
 *      Events Manager > Data Sources. When blank, no snippet is loaded
 *      and every call is a no-op (cheap default for contributors).
 *   2. {@link initMetaPixel} loads the official Meta snippet, fires
 *      one `PageView`, and from then on `metaPixelTrack(...)`
 *      forwards through `fbq("trackCustom", ...)`.
 *   3. {@link metaCapiSend} (server-paired) optionally mirrors the
 *      highest-value events to the Cloud Functions Conversions API
 *      proxy with the same `event_id` for dedup.
 *
 * Browser security: the pixel id is intentionally PUBLIC — it ships in
 * the HTML of every Meta-integrated page on the web. The Conversions
 * API access token is NOT — it lives only as a Cloud Functions secret
 * (`META_CAPI_ACCESS_TOKEN`).
 */

import type { AttributionBundle } from "./attribution";

/** Standard Meta events we map our funnel onto. */
export type MetaStandardEvent =
  | "PageView"
  | "ViewContent"
  | "Lead"
  | "CompleteRegistration"
  | "InitiateCheckout"
  | "Purchase"
  | "Subscribe"
  | "StartTrial";

/**
 * Subset of the `fbq` global the snippet installs on `window`. We type
 * just the calls we make so a typo gets caught at the call site without
 * pulling in a heavy `@types/facebook-pixel` ambient declaration.
 *
 * `callMethod` is patched in by Meta's loader (`fbevents.js`) once it
 * arrives — typed as optional so the stub we install pre-loader (which
 * only has `queue`) is still a valid `FbqFn`.
 */
type FbqFn = {
  (cmd: "init", pixelId: string, params?: Record<string, unknown>): void;
  (cmd: "set", topic: string, key: string, value: unknown): void;
  (
    cmd: "track" | "trackCustom",
    eventName: string,
    params?: Record<string, unknown>,
    options?: { eventID?: string }
  ): void;
  queue?: unknown[];
  loaded?: boolean;
  version?: string;
  callMethod?: (...args: unknown[]) => void;
};

declare global {
  interface Window {
    fbq?: FbqFn;
    _fbq?: FbqFn;
  }
}

let pixelId: string | undefined;
let ready = false;
let initialized = false;

function readEnv(name: string): string | undefined {
  const v = import.meta.env[name as keyof ImportMetaEnv] as string | undefined;
  if (typeof v !== "string") return undefined;
  const trimmed = v.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Inject the canonical Meta Pixel base code. Verbatim from the
 * Pixel install instructions in Events Manager — Meta won't accept
 * a paraphrased version (it pattern-matches the snippet to mark the
 * pixel as "installed correctly" in their UI).
 *
 * Done lazily (only when a real pixel id is configured) so unit tests
 * and contributor builds without env vars never touch `window`.
 */
function injectPixelSnippet(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (window.fbq) return;
  // The pattern below is the canonical Meta Pixel base code. Keep it
  // shape-identical to what Events Manager generates so future Meta
  // SDK updates don't reject ours as malformed.
  const n: FbqFn = function (this: unknown, ...args: unknown[]) {
    // The queue is consumed by the loader once the real `fbevents.js`
    // arrives — calls made between this stub install and the real
    // function being defined are replayed in order.
    if (n.callMethod) {
      n.callMethod.apply(n, args);
    } else {
      n.queue!.push(args);
    }
  } as FbqFn;
  n.queue = [];
  n.loaded = true;
  n.version = "2.0";
  window.fbq = n;
  window._fbq ??= n;

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  const first = document.getElementsByTagName("script")[0];
  if (first?.parentNode) {
    first.parentNode.insertBefore(script, first);
  } else {
    document.head.appendChild(script);
  }
}

/**
 * Boot the Meta Pixel. Idempotent. No-op when `VITE_META_PIXEL_ID` is
 * unset (parity with `initTelemetry`). Fires exactly one `PageView`
 * automatically — subsequent SPA route changes do NOT — see
 * {@link metaPixelTrack} for the manual path.
 */
export function initMetaPixel(): void {
  if (initialized) return;
  initialized = true;
  pixelId = readEnv("VITE_META_PIXEL_ID");
  if (!pixelId) return;
  injectPixelSnippet();
  if (!window.fbq) return;
  window.fbq("init", pixelId);
  // The auto-PageView is the conventional first event. It's required
  // for Meta to mark the Pixel as "Active" in Events Manager.
  window.fbq("track", "PageView");
  ready = true;
}

/**
 * Stamp UTM context into the Pixel's "data" envelope so subsequent
 * events carry the same campaign attribution. Meta calls this
 * "advanced matching" — we use the UTM fields as a poor-man's match
 * key (only the standard event payload supports `eventID` /
 * `event_id`; user identifiers ride here).
 *
 * Called from {@link import("./attribution").initAttribution} once
 * per session, immediately after the URL UTM bundle is captured.
 */
export function setMetaPixelUtmAdvancedMatching(b: AttributionBundle): void {
  if (!ready || !window.fbq) return;
  // `set` with key `userData` is the documented advanced-matching
  // entry point; we keep the payload shape conservative so future
  // Pixel SDK versions don't reject extra fields.
  const userData: Record<string, string> = {};
  if (b.fbclid) userData.fbc = `fb.1.${b.at}.${b.fbclid}`;
  if (Object.keys(userData).length === 0) return;
  try {
    window.fbq("set", "userData", "auto-match", userData);
  } catch {
    // Older Pixel SDKs may not support `set` for userData — fine to drop.
  }
}

/**
 * Fire a standard or custom Meta Pixel event. Standard events
 * (PageView, ViewContent, Lead, …) hit the platform's optimized
 * reporting path; anything else routes through `trackCustom` which
 * Meta still reports but isn't usable as an optimization goal.
 *
 * Pass an `eventId` to deduplicate against a paired Conversions-API
 * call from the server: both must use the same `event_id` within a
 * 48h window for Meta to count them as one. See
 * {@link import("../../functions/src/metaCapi").metaCapiTrack}.
 */
export function metaPixelTrack(
  eventName: MetaStandardEvent | string,
  params?: Record<string, unknown>,
  eventId?: string
): void {
  if (!ready || !window.fbq) return;
  try {
    const cmd = isStandardEvent(eventName) ? "track" : "trackCustom";
    const options = eventId ? { eventID: eventId } : undefined;
    window.fbq(cmd, eventName, params ?? {}, options);
  } catch {
    // never let a Pixel blip propagate into product code
  }
}

const STANDARD_EVENTS = new Set<string>([
  "PageView",
  "ViewContent",
  "Lead",
  "CompleteRegistration",
  "InitiateCheckout",
  "Purchase",
  "Subscribe",
  "StartTrial",
]);

function isStandardEvent(name: string): name is MetaStandardEvent {
  return STANDARD_EVENTS.has(name);
}

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  for (const raw of document.cookie.split(";")) {
    const part = raw.trim();
    const eq = part.indexOf("=");
    if (eq === -1) continue;
    if (part.slice(0, eq) === name) {
      const value = decodeURIComponent(part.slice(eq + 1));
      return value.length > 0 ? value : undefined;
    }
  }
  return undefined;
}

/** Pixel `_fbp` / `_fbc` cookies, read at send time (the snippet writes
 *  them asynchronously after `init`). */
export function readMetaPixelCookies(): { fbp?: string; fbc?: string } {
  const fbp = readCookie("_fbp");
  const fbc = readCookie("_fbc");
  return {
    ...(fbp ? { fbp } : {}),
    ...(fbc ? { fbc } : {}),
  };
}

/**
 * Click ids for CAPI `user_data`. Prefer Pixel cookies; if `_fbc` is
 * missing, reconstruct from a captured `fbclid`.
 */
export async function resolveMetaClickIds(): Promise<{
  fbp?: string;
  fbc?: string;
}> {
  const cookies = readMetaPixelCookies();
  if (cookies.fbc) return cookies;
  try {
    const { getLastTouch, getFirstTouch } = await import("./attribution");
    const touch = getLastTouch() ?? getFirstTouch();
    if (touch?.fbclid) {
      return {
        ...cookies,
        fbc: `fb.1.${touch.at}.${touch.fbclid}`,
      };
    }
  } catch {
    // attribution module unavailable — cookies only
  }
  return cookies;
}

/** Events Manager Test Events code from `?meta_test=TEST…`. */
export function readMetaTestEventCode(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const v = new URLSearchParams(window.location.search).get("meta_test");
    if (!v) return undefined;
    const trimmed = v.trim();
    return trimmed.length > 0 ? trimmed.slice(0, 64) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Mint a stable per-event id used for Pixel <-> Conversions-API
 * dedup. Format must match what the server sends in
 * `event_id` field of the CAPI payload — verbatim, byte-for-byte.
 */
export function makeMetaEventId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  // Fallback for very old browsers / non-secure contexts (testing).
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Send the same event payload to the server-side Conversions API proxy
 * (a Firebase callable). The server forwards to
 * `graph.facebook.com/.../events` with the long-lived access token —
 * the token NEVER ships in the browser bundle.
 *
 * Failure is silently dropped: CAPI is a secondary attribution channel,
 * the Pixel call is the source of truth for awareness campaigns.
 */
export async function metaCapiSend(
  eventName: MetaStandardEvent | string,
  eventId: string,
  params?: Record<string, unknown>
): Promise<void> {
  if (!ready) return;
  try {
    // Lazy import the Functions module so the call site doesn't pay
    // the bundle cost on the cold path.
    const { getFunctions, httpsCallable } = await import("firebase/functions");
    const { getFirebaseApp } = await import("./firebase");
    const app = getFirebaseApp();
    if (!app) return;
    const functions = getFunctions(
      app,
      readEnv("VITE_FIREBASE_FUNCTIONS_REGION") ?? "us-central1"
    );
    const fn = httpsCallable(functions, "metaCapiTrack");
    const clickIds = await resolveMetaClickIds();
    const testEventCode = readMetaTestEventCode();
    await fn({
      eventName,
      eventId,
      params: params ?? {},
      sourceUrl: typeof window !== "undefined" ? window.location.href : null,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      fbp: clickIds.fbp ?? null,
      fbc: clickIds.fbc ?? null,
      testEventCode: testEventCode ?? null,
    });
  } catch {
    // CAPI is best-effort; drop on any failure.
  }
}

/** Test-only: tear down for clean test boots. */
export function _resetMetaPixelForTests(): void {
  pixelId = undefined;
  ready = false;
  initialized = false;
  if (typeof window !== "undefined") {
    delete window.fbq;
    delete window._fbq;
  }
}
